import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import moment from "moment";

import { BIBlue } from "../../../assets/helpers";
import { fetchClientInventoryFormResponses2, fetchClientInventoryCheckAnswers2, fetchClientInventoryCheckPictures2 } from "../../inventory-check/inventoryCheckQuestionsService";
import PictureViewer from "../../../common/PictureViewer";

const getStatusColors = (label) => {
  if (!label) return { bgcolor: "#F1F5F9", color: "#475569", border: "#CBD5E1" };
  const key = label.toLowerCase().trim();
  switch (key) {
    case "active":
    case "verified - all correct":
    case "verified":
    case "found":
      return { bgcolor: "#ECFDF5", color: "#047857", border: "#A7F3D0" };
    case "inactive":
    case "retired":
    case "decommissioned":
    case "disposed":
      return { bgcolor: "#F1F5F9", color: "#334155", border: "#CBD5E1" };
    case "missing":
    case "not found":
    case "lost":
      return { bgcolor: "#FEF2F2", color: "#B91C1C", border: "#FECACA" };
    case "discrepancies corrected":
    case "location updated":
    case "assignment updated":
      return { bgcolor: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };
    case "damaged":
    case "repair":
    case "defective":
      return { bgcolor: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    default:
      return { bgcolor: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
  }
};

const FixedAssetInventoryCheck = ({ selectedAsset }) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main);
  const userId = loginData?.userInformation?.UserId || 1;
  const clientId = selectedClientData?.ClientId;
  const accentColor = selectedClientData?.AccentColor || BIBlue;

  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedHistoryDetail, setSelectedHistoryDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [pictureViewerOpen, setPictureViewerOpen] = useState(false);
  const [pictureViewerIndex, setPictureViewerIndex] = useState(0);
  const [pictureViewerList, setPictureViewerList] = useState([]);

  const loadHistoryRecords = useCallback(async () => {
    if (!selectedAsset?.AssetId || !clientId) return;
    
    setHistoryLoading(true);
    try {
      const searchList = [
        {
          ClientId: Number(clientId)
        },
        {
          assetId: Number(selectedAsset.AssetId)
        }
      ];

      const res = await fetchClientInventoryFormResponses2({
        userId: Number(userId),
        nrOfRecPerPage: 100,
        searchList,
        sortList: [{ FieldName: "Id", Direction: "DESC" }],
        token: loginData?.accessToken
      });

      const respData = res?.data?.data || res?.data || [];
      const list = Array.isArray(respData) ? respData : [];

      const apiRecords = list.map((item) => {
        const respId = Number(item.Id ?? item.id ?? item.FormResponseId ?? item.form_response_id ?? 0);
        return {
          id: respId,
          formResponseId: respId,
          assetId: item.AssetId ?? item.assetId,
          assetCode: item.AssetCode || item.assetCode || selectedAsset.AssetCode || "N/A",
          assetName: item.AssetName || item.assetName || selectedAsset.AssetName || "Fixed Asset",
          categoryName: item.CategoryName ?? item.categoryName ?? selectedAsset.CategoryName ?? "",
          subCategoryName: item.SubCategoryName ?? item.subCategoryName ?? selectedAsset.SubCategoryName ?? "",
          serialNo: item.SerialNo ?? item.serialNo ?? selectedAsset.SerialNo ?? "N/A",
          dateTime: item.CreatedDate ?? item.created_date ?? item.Date ?? item.dateTime ?? item.ModifiedDate ?? item.modified_date ?? new Date().toISOString(),
          auditorName: item.ClientEmployeeName ?? item.clientEmployeeName ?? item.AuditorName ?? item.auditorName ?? item.ModifiedByName ?? "Auditor",
          auditorId: item.ClientEmployeeId ?? item.clientEmployeeId ?? item.ModifiedBy ?? item.modifiedBy ?? userId,
          resultStatus: item.ResultStatus ?? item.resultStatus ?? "Verified",
          location: item.LocationTitle ?? item.locationTitle ?? item.Location ?? item.location ?? selectedAsset.LocationTitle ?? "Not Specified",
          assignedTo: item.AssignedToName ?? item.assignedToName ?? item.EmployeeName ?? "Not Assigned",
          discrepancies: item.Discrepancies ?? item.discrepancies ?? "",
          notes: item.Note ?? item.note ?? item.Notes ?? item.notes ?? item.Comments ?? item.comments ?? "Inventory check completed.",
          photos: Array.isArray(item.photos) ? item.photos : [],
          formId: item.FormId ?? item.formId ?? null,
          formName: item.FormName ?? item.formName ?? "Inspection Check",
          formDescription: item.FormDescription ?? item.formDescription ?? "",
          organizationName: item.OrganizationName ?? item.organizationName ?? "",
          questionResponses: Array.isArray(item.questionResponses) ? item.questionResponses : []
        };
      });
      setHistoryList(apiRecords);
    } catch (err) {
      console.warn("Error fetching form responses:", err);
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [clientId, userId, loginData?.accessToken, selectedAsset]);

  useEffect(() => {
    loadHistoryRecords();
  }, [loadHistoryRecords]);

  const handleOpenHistoryDetail = async (historyItem) => {
    setSelectedHistoryDetail(historyItem);
    const formResponseId = historyItem.formResponseId || historyItem.id;

    if (!formResponseId) return;

    setDetailLoading(true);
    try {
      const ansRes = await fetchClientInventoryCheckAnswers2({
        userId: Number(userId || 1),
        nrOfRecPerPage: 100,
        searchList: [{ formResponseId: Number(formResponseId) }],
        token: loginData?.accessToken
      });

      const ansData = ansRes?.data?.data || ansRes?.data || [];
      const answersList = Array.isArray(ansData) ? ansData : [];

      let picturesList = [];
      try {
        const picRes = await fetchClientInventoryCheckPictures2({
          userId: Number(userId || 1),
          nrOfRecPerPage: 50,
          searchList: [{ formResponseId: Number(formResponseId) }],
          token: loginData?.accessToken
        });
        const picData = picRes?.data?.data || picRes?.data || [];
        const rawPics = Array.isArray(picData) ? picData : [];
        picturesList = rawPics
          .map((p) => p.FileUrl || p.fileUrl || p.ImageUrl || p.imageUrl || p.url || p.PictureUrl || p.pictureUrl)
          .filter(Boolean);
      } catch (pErr) {
        console.warn("Fetch check pictures API warning:", pErr);
      }

      if (answersList.length > 0 || picturesList.length > 0) {
        const mappedResponses = answersList.map((a, idx) => {
          const expVal = String(a.ExpectedValue ?? a.expectedValue ?? a.expected_value ?? "");
          const corrVal = String(a.CorrectedValue ?? a.correctedValue ?? a.corrected_value ?? a.expcted_value ?? expVal);
          const expType = String(a.ExpectedValueType ?? a.expectedValueType ?? a.expected_value_type ?? "");
          const corrType = String(a.CorrectedValueType ?? a.correctedValueType ?? a.corrected_value_type ?? "");
          const note = String(a.CorrectionNote ?? a.correctionNote ?? a.correction_note ?? "");
          const qId = Number(a.QuestionId ?? a.questionId ?? a.question_id ?? idx + 1);

          const qTitle = String(a.QuestionTitle ?? a.questionTitle ?? a.Question ?? a.Title ?? `Question #${idx + 1}`);

          const normExp = expVal.trim().toLowerCase();
          const normCorr = corrVal.trim().toLowerCase();
          const normNote = note.trim().toLowerCase();
          const normTitle = qTitle.trim().toLowerCase();

          let isCorrect = true;
          if (typeof a.isCorrect === "boolean") {
            isCorrect = a.isCorrect;
          } else if (normExp === normCorr && normCorr !== "") {
            isCorrect = true;
          } else {
            const isTruthExp = ["true", "yes", "1", "ok", "verified", "correct"].includes(normExp);
            const isTruthCorr = ["true", "yes", "1", "ok", "verified", "correct"].includes(normCorr);

            if (isTruthExp && isTruthCorr) {
              isCorrect = true;
            } else if ((normExp.includes("?") || normExp === normTitle) && isTruthCorr && (!normNote || normNote === "yes" || normNote === "true")) {
              isCorrect = true;
            } else {
              isCorrect = false;
            }
          }

          return {
            questionId: qId,
            questionTitle: qTitle,
            questionSubTitle: a.QuestionSubTitle ?? a.questionSubTitle ?? a.SubTitle ?? "",
            questionCode: a.QuestionCode ?? a.questionCode ?? a.Code ?? "",
            expectedValue: expVal,
            expectedValueType: expType,
            correctedValue: corrVal,
            correctedValueType: corrType,
            correctionNote: note,
            isCorrect
          };
        });

        setSelectedHistoryDetail((prev) => ({
          ...prev,
          questionResponses: mappedResponses.length > 0 ? mappedResponses : prev?.questionResponses,
          photos: picturesList.length > 0 ? picturesList : (prev?.photos || [])
        }));
      }
    } catch (err) {
      console.error("Error fetching submitted answers:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredHistory = historyList.filter((item) => {
    if (historySearch) {
      const q = historySearch.toLowerCase();
      return (
        (item.assetCode || "").toLowerCase().includes(q) ||
        (item.assetName || "").toLowerCase().includes(q) ||
        (item.auditorName || "").toLowerCase().includes(q) ||
        (item.location || "").toLowerCase().includes(q) ||
        (item.resultStatus || "").toLowerCase().includes(q) ||
        (item.notes || "").toLowerCase().includes(q) ||
        (item.formName || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <Box sx={{ pt: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Inventory Check History
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search history..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9499A8", fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              bgcolor: "#FAFBFC",
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "8px",
                fontSize: "0.82rem"
              }
            }}
          />
          <IconButton onClick={loadHistoryRecords} sx={{ width: 38, height: 38, bgcolor: "#F5F6FA", borderRadius: "8px", color: accentColor }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {historyLoading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} sx={{ color: accentColor }} />
        </Box>
      ) : filteredHistory.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
          No inventory checks recorded yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {filteredHistory.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              onClick={() => handleOpenHistoryDetail(item)}
              sx={{
                p: 2,
                borderRadius: "12px",
                borderColor: "#ECEEF2",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: accentColor,
                  bgcolor: "#FAFBFC"
                }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.8 }}>
                  <Typography component="span" sx={{ fontWeight: 750, color: "#111827", fontSize: "0.94rem" }}>
                    {item.assetCode && item.assetCode !== "N/A" ? item.assetCode : item.assetName}
                  </Typography>
                  <Chip
                    size="small"
                    label={item.resultStatus || "Verified"}
                    sx={{
                      height: 20,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      borderRadius: "6px",
                      ...getStatusColors(item.resultStatus)
                    }}
                  />
                  {item.formName && (
                    <Chip
                      size="small"
                      label={item.formName}
                      sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, borderRadius: "6px", bgcolor: "#F1F5F9", color: "#475569" }}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={2.5} sx={{ mt: 0.8 }}>
                  <Typography variant="caption" sx={{ color: "#64748B" }}>
                    <strong>Auditor:</strong> {item.auditorName}
                  </Typography>
                  {item.location && item.location !== "Not Specified" && (
                    <Typography variant="caption" sx={{ color: "#121e30" }}>
                      <strong>Location:</strong> {item.location}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "#64748B" }}>
                    <strong>Date:</strong> {moment(item.dateTime).format("MMM DD, YYYY")}
                  </Typography>
                </Stack>
                {item.notes && (
                  <Typography variant="caption" sx={{ color: "#475569", display: "block", mt: 0.6, fontWeight: 500 }}>
                    "{item.notes}"
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {item.photos && item.photos.length > 0 && (
                  <Chip
                    size="small"
                    icon={<AddPhotoAlternateIcon sx={{ fontSize: 13 }} />}
                    label={item.photos.length}
                    sx={{ height: 22, fontSize: "0.72rem", bgcolor: "#F5F6FA", fontWeight: 600, borderRadius: "6px" }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenHistoryDetail(item);
                  }}
                  endIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    borderColor: "#ECEEF2",
                    color: "#4B5563",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.4
                  }}
                >
                  Details
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {selectedHistoryDetail && (
        <Dialog open={Boolean(selectedHistoryDetail)} onClose={() => setSelectedHistoryDetail(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2.5, bgcolor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 42, height: 42, borderRadius: "10px", bgcolor: `${accentColor}18`, color: accentColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FactCheckIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1.05rem" }}>
                    {selectedHistoryDetail.formName || "Inspection Check"}
                  </Typography>
                  <Chip size="small" label={selectedHistoryDetail.resultStatus || "Verified"} sx={{ fontWeight: 700, height: 22, borderRadius: "6px", fontSize: "0.72rem", ...getStatusColors(selectedHistoryDetail.resultStatus) }} />
                </Stack>
                <Typography variant="caption" sx={{ color: "#64748B", mt: 0.3, display: "block" }}>
                  Record ID: #{selectedHistoryDetail.id || selectedHistoryDetail.formResponseId}
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setSelectedHistoryDetail(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: "12px", bgcolor: "#FFFFFF" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                    <Box sx={{ p: 0.6, borderRadius: "6px", bgcolor: "#F1F5F9", color: "#475569", display: "flex" }}>
                      <InventoryIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Asset Details</Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: "0.92rem" }}>
                    {selectedHistoryDetail.assetCode} - {selectedHistoryDetail.assetName}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: "12px", bgcolor: "#FFFFFF" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                    <Box sx={{ p: 0.6, borderRadius: "6px", bgcolor: "#F1F5F9", color: "#475569", display: "flex" }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Auditor</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: "#0F172A" }}>
                    {selectedHistoryDetail.auditorName || "Field Auditor"}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2.2, borderRadius: "12px", bgcolor: "#FAFAFA" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                    <Box sx={{ p: 0.6, borderRadius: "6px", bgcolor: "#FFFFFF", color: accentColor, display: "flex", border: "1px solid #E2E8F0" }}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Audit Note</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: "#1E293B", fontSize: "0.84rem", fontStyle: selectedHistoryDetail.notes ? "normal" : "italic" }}>
                    {selectedHistoryDetail.notes ? `"${selectedHistoryDetail.notes}"` : "No notes recorded."}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                  <PlaylistAddCheckIcon sx={{ fontSize: 20, color: accentColor }} />
                  <Typography sx={{ fontSize: "0.78rem", color: "#334155", fontWeight: 750, textTransform: "uppercase" }}>
                    INSPECTION CHECKLIST RESPONSES
                  </Typography>
                  {detailLoading && <CircularProgress size={14} sx={{ color: accentColor }} />}
                </Stack>
                
                {detailLoading ? (
                  <Box sx={{ py: 4, textAlign: "center", bgcolor: "#F8FAFC", borderRadius: "10px" }}>
                    <CircularProgress size={22} sx={{ color: accentColor }} />
                  </Box>
                ) : selectedHistoryDetail.questionResponses?.length > 0 ? (
                  <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden" }}>
                    <Stack divider={<Divider sx={{ borderColor: "#F1F5F9" }} />}>
                      {selectedHistoryDetail.questionResponses.map((qr, idx) => (
                        <Box key={idx} sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, bgcolor: idx % 2 === 0 ? "#FFFFFF" : "#F8FAFC" }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#0F172A" }}>
                              {qr.questionTitle || qr.title || `Question #${idx + 1}`}
                            </Typography>
                            {qr.isCorrect === false && (
                              <Box sx={{ mt: 1, p: 1.2, bgcolor: "#FEF2F2", borderRadius: "8px", border: "1px solid #FEE2E2" }}>
                                <Typography sx={{ fontSize: "0.78rem", color: "#991B1B", fontWeight: 600 }}>
                                  Expected: <span style={{ textDecoration: "line-through", color: "#6B7280" }}>{qr.expectedValue || "N/A"}</span> &rarr; Corrected: <strong style={{ color: "#DC2626" }}>{qr.correctedValue || "N/A"}</strong>
                                </Typography>
                                {qr.correctionNote && <Typography sx={{ fontSize: "0.74rem", color: "#B91C1C", mt: 0.4, fontStyle: "italic" }}>Note: {qr.correctionNote}</Typography>}
                              </Box>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={qr.isCorrect !== false ? String(qr.correctedValue || qr.expectedValue || "Yes") : "DISCREPANCY"}
                            sx={{ fontWeight: 700, fontSize: "0.74rem", borderRadius: "6px", bgcolor: qr.isCorrect !== false ? "#DCFCE7" : "#FEE2E2", color: qr.isCorrect !== false ? "#15803D" : "#B91C1C" }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center", borderRadius: "10px", bgcolor: "#F8FAFC" }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#94A3B8", fontStyle: "italic" }}>No checklist answers recorded.</Typography>
                  </Paper>
                )}
              </Box>

              {selectedHistoryDetail.photos?.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: "0.74rem", color: "#475569", fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
                    ATTACHED PHOTOS ({selectedHistoryDetail.photos.length})
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    {selectedHistoryDetail.photos.map((src, i) => (
                      <Box
                        key={i}
                        component="img"
                        src={src}
                        sx={{ width: 80, height: 80, borderRadius: "10px", objectFit: "cover", cursor: "pointer", border: "2px solid #E2E8F0" }}
                        onClick={() => {
                          setPictureViewerList(selectedHistoryDetail.photos.map((p) => ({ src: p })));
                          setPictureViewerIndex(i);
                          setPictureViewerOpen(true);
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 3, bgcolor: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
            <Button variant="contained" onClick={() => setSelectedHistoryDetail(null)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3, bgcolor: accentColor }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {pictureViewerOpen && (
        <PictureViewer isOpen={pictureViewerOpen} onClose={() => setPictureViewerOpen(false)} pictures={pictureViewerList} viewerIndex={pictureViewerIndex} />
      )}
    </Box>
  );
};

export default FixedAssetInventoryCheck;
